import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Call, CallOutcome } from '@/types/calls'
import { ArrowUpDown } from 'lucide-react'
import { useState } from 'react'
import { CallRow } from './call-row'

interface CallsTableProps {
  calls: Call[]
  category: CallOutcome | 'all'
  onCallReview: (sessionId: string, reviewed: boolean) => void
  onCallDetails: (call: Call) => void
}

type SortDirection = 'asc' | 'desc'

export function CallsTable({ calls, category, onCallReview, onCallDetails }: CallsTableProps) {
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [selectedCalls, setSelectedCalls] = useState<Set<string>>(new Set())

  // Sort calls by time
  const sortedCalls = [...calls].sort((a, b) => {
    const dateA = new Date(a.startTime).getTime()
    const dateB = new Date(b.startTime).getTime()
    return sortDirection === 'desc' ? dateB - dateA : dateA - dateB
  })

  const toggleSort = () => {
    setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')
  }

  const toggleCallSelection = (sessionId: string) => {
    const newSelection = new Set(selectedCalls)
    if (newSelection.has(sessionId)) {
      newSelection.delete(sessionId)
    } else {
      newSelection.add(sessionId)
    }
    setSelectedCalls(newSelection)
  }

  const toggleAllSelection = () => {
    if (selectedCalls.size === sortedCalls.length) {
      setSelectedCalls(new Set())
    } else {
      setSelectedCalls(new Set(sortedCalls.map(c => c.sessionId)))
    }
  }

  const isAllSelected = selectedCalls.size === sortedCalls.length && sortedCalls.length > 0

  return (
    <div>
      {/* Main calls table - no border/shadow as it's inside the box */}
      <div className="bg-white overflow-hidden">
        <div className="max-h-[calc(100vh-280px)] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-gray-50">
              <TableRow className="border-b border-gray-200 bg-gray-50 hover:bg-gray-50">
                <TableHead className="w-12 h-14 pl-6">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={toggleAllSelection}
                    aria-label="Select all calls"
                  />
                </TableHead>
                <TableHead className="h-14">
                  <Button
                    variant="ghost"
                    onClick={toggleSort}
                    className="-ml-4 flex h-9 items-center gap-2 px-3 hover:bg-gray-100 rounded"
                  >
                    <span className="text-base font-semibold text-gray-800">Call time</span>
                    <ArrowUpDown className="h-4 w-4 text-gray-600" />
                  </Button>
                </TableHead>
                <TableHead className="h-14 text-base font-semibold text-gray-800">Result</TableHead>
                <TableHead className="h-14 text-base font-semibold text-gray-800">Caller</TableHead>
                <TableHead className="h-14 text-base font-semibold text-gray-800">Booking time</TableHead>
                <TableHead className="h-14 text-base font-semibold text-gray-800">Guests</TableHead>
                <TableHead className="h-14 text-base font-semibold text-gray-800">Booking ID</TableHead>
                <TableHead className="w-32 h-14 pr-6 text-right text-base font-semibold text-gray-800">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedCalls.length === 0 ? (
                <TableRow>
                  <td colSpan={8} className="h-32 text-center">
                    <div className="text-gray-500 text-sm">
                      {category === 'all'
                        ? 'No calls found for this period'
                        : 'No calls match your selected filters'}
                    </div>
                  </td>
                </TableRow>
              ) : (
                sortedCalls.map(call => (
                  <CallRow
                    key={call.sessionId}
                    call={call}
                    selected={selectedCalls.has(call.sessionId)}
                    onSelect={() => toggleCallSelection(call.sessionId)}
                    onReview={(reviewed) => onCallReview(call.sessionId, reviewed)}
                    onDetails={() => onCallDetails(call)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  )
}
